/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useRef, useEffect } from 'react';
import { IconButton } from "@/subframe/components/IconButton";
import { DialogLayout } from "@/subframe/layouts/DialogLayout";
import { Tooltip } from "@/subframe/components/Tooltip";
import { Button } from "@/subframe/components/Button";
import SubframeCore from "@subframe/core";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../redux/store";
import { handleMediaSend } from "../../redux/reducers/mediaMessageSlice";
import SelectDateAndTimePopup from '../subcomponents/SelectDateAndTimePopup';
import { addNotification } from "../../redux/reducers/notificationSlice";
import { NotificationIcon, NotificationType } from "../../Utils/notificationEnums";
import moment from "moment";
import VoiceMessage from './VoiceMessage';
import RecordRTC from 'recordrtc';
import { getAuthToken } from "../../Utils/authToken";

interface VoiceRecorderProps {
  currentRecipient: any;
  onClose?: () => void;
}

const VoiceRecorder: React.FC<VoiceRecorderProps> = ({ currentRecipient, onClose }) => {
  const dispatch = useDispatch<AppDispatch>();
  const system_user_id = useSelector((state: RootState) => state.auth.system_user_id);
  const isMediaUploading = useSelector((state: RootState) => state.mediaMessage.isLoading);

  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [isDateTimePopupOpen, setIsDateTimePopupOpen] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<any>(null);

  // Replace MediaRecorder with RecordRTC
  const recordRTCRef = useRef<RecordRTC | null>(null);
  const microphoneStreamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Request microphone permission and set up recorder
  useEffect(() => {
    const setupRecorder = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        microphoneStreamRef.current = stream;
        
        // We'll initialize RecordRTC when starting recording, not here
          } catch (error) {
        dispatch(
          addNotification({
            id: Date.now(),
            type: NotificationType.ERROR,
            title: "Microphone Access Failed",
            message: "Please allow microphone access to record voice messages",
            icon: NotificationIcon.ERROR,
          })
        );
      }
    };

    setupRecorder();

    // Cleanup
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
      stopRecording();
      
      // Stop and release microphone on unmount
      if (microphoneStreamRef.current) {
        microphoneStreamRef.current.getTracks().forEach(track => {
          track.stop();
        });
      }
    };
  }, [dispatch]);

  const startRecording = (): void => {
    if (microphoneStreamRef.current && !isRecording) {
      // Define the RecordRTC options type explicitly
      const recordRTCOptions: RecordRTC.Options = {
        type: 'audio',
        mimeType: 'audio/mp3' as any, // Type assertion required due to RecordRTC types
        recorderType: RecordRTC.StereoAudioRecorder,
        numberOfAudioChannels: 1, // Mono
        desiredSampRate: 44100,   // Standard sample rate
        bitrate: 128,             // 128 kbps - fixed lowercase 'r'
        bufferSize: 16384,        // Increased buffer size for better quality
      };
      
      // Initialize RecordRTC with proper options for WhatsApp compatibility
      recordRTCRef.current = new RecordRTC(
        microphoneStreamRef.current, 
        recordRTCOptions
      );
      
      recordRTCRef.current.startRecording();
      
      setRecordingTime(0);
      setIsRecording(true);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime((prevTime) => prevTime + 1);
      }, 1000);
    }
  };

  const stopRecording = () => {
    if (recordRTCRef.current && isRecording) {
      setIsRecording(false);
      
      // Stop timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      
      // Stop recording and process the result
      recordRTCRef.current.stopRecording(() => {
        const blob = recordRTCRef.current?.getBlob();
        if (blob) {
          // Explicitly create a new blob with the mp3 mime type for WhatsApp compatibility
          const audioBlob = new Blob([blob], { type: 'audio/mp3' });
          const audioUrl = URL.createObjectURL(audioBlob);         
          setAudioBlob(audioBlob);
          setAudioUrl(audioUrl);
          setShowPreview(true);
        }
      });
    }
  };
  
  const uploadVoiceMessage = async () => {
    if (!audioBlob) return;

    try {
      setIsUploading(true);
      setUploadError(null);
      const authToken = getAuthToken();
      
      const formData = new FormData();
      // Use a timestamp for unique file naming
      formData.append('file', audioBlob, `voice_message_${Date.now()}.mp3`);
      
      const response = await fetch('https://api.insightssystem.com/api:uYdlVwac/upload_file_by_attachment', {
        method: 'POST',
        headers: {
          'Accept': '*/*',
          'Authorization': `Bearer ${authToken}`
        },
        body: formData
      });

      const result = await response.json();

      if (result.success) {
        setUploadedFile(result);
      } else {
        setUploadError(result.message || result.error || 'Upload failed');
        
        dispatch(
          addNotification({
            id: Date.now(),
            type: NotificationType.ERROR,
            title: "WhatsApp Voice Message Validation Failed",
            message: result.message || "File doesn't meet WhatsApp requirements",
            icon: NotificationIcon.ERROR,
          })
        );
      }
    } catch (error: any) {
      setUploadError('Failed to upload voice message. Please try again.');
      dispatch(
        addNotification({
          id: Date.now(),
          type: NotificationType.ERROR,
          title: "Upload Failed",
          message: error.message || "Something went wrong during upload.",
          icon: NotificationIcon.ERROR,
        })
      );
    } finally {
      setIsUploading(false);
    }
  };

  useEffect(() => {
    if (audioBlob && showPreview) {
      uploadVoiceMessage();
    }
  }, [audioBlob, showPreview]);

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  const handleSend = async (scheduledTime?: number) => {
    if (!uploadedFile || !system_user_id || !currentRecipient) return;

    try {
      await dispatch(handleMediaSend({
        media_link: uploadedFile.file_url,
        type: "audio", // Specify audio type
        caption: "", // Voice messages don't need captions
        currentRecipient,
        ...(scheduledTime ? { scheduled_time: scheduledTime } : {})
      })).unwrap();

      // Show success notification based on whether scheduled or not
      if (scheduledTime) {
        const formattedDateTime = moment(scheduledTime).format('MMM D, YYYY hh:mm A');
        dispatch(
          addNotification({
            id: Date.now(),
            type: NotificationType.SUCCESS,
            title: "Voice Message Scheduled",
            message: `Your voice message will be sent on ${formattedDateTime}`,
            icon: NotificationIcon.SUCCESS,
          })
        );
      } else {
        dispatch(
          addNotification({
            id: Date.now(),
            type: NotificationType.SUCCESS,
            title: "Voice Message Sent",
            message: "Your voice message has been sent successfully",
            icon: NotificationIcon.SUCCESS,
          })
        );
      }

      // Clear states after successful send
      setShowPreview(false);
      setAudioBlob(null);
      setAudioUrl(null);
      setUploadedFile(null);
      setUploadError(null);
      
      if (onClose) {
        onClose();
      }
    } catch (error: any) {
      // Show error notification
      const errorTitle = scheduledTime ? "Schedule Failed" : "Voice Message Not Sent";
      dispatch(
        addNotification({
          id: Date.now(),
          type: NotificationType.ERROR,
          title: errorTitle,
          message: error.message || "Something went wrong. Please try again.",
          icon: NotificationIcon.ERROR,
        })
      );

    }
  };

  const handleScheduleMessage = (scheduledTime: number) => {
    // Ignore messageText parameter since voice messages don't have captions
    handleSend(scheduledTime);
  };

  const handleCancel = () => {
    setShowPreview(false);
    setAudioBlob(null);
    setAudioUrl(null);
    setUploadedFile(null);
    if (onClose) {
      onClose();
    }
  };

  // Recording button UI
  const renderRecordingButton = () => {
    if (isRecording) {
      return (
        <div className="flex flex-col items-center gap-2">
          <div className="flex items-center gap-4">
            <div className="h-4 w-4 rounded-full bg-red-500 animate-pulse"></div>
            <span className="text-body font-body text-default-font">
              {formatTime(recordingTime)}
            </span>
          </div>
          <Button 
            variant="destructive-secondary"
            icon="FeatherSquare"
            onClick={stopRecording}
          >
            Stop Recording
          </Button>
        </div>
      );
    }
    
    return (
      <Button 
        variant="brand-primary"
        icon="FeatherMic"
        onClick={startRecording}
      >
        Start Recording
      </Button>
    );
  };

  return (
    <>
      <DialogLayout open={!showPreview && !audioUrl} onOpenChange={() => {
        if (onClose) onClose();
      }}>
        <div className="flex h-full w-full flex-col items-center justify-center gap-6 bg-default-background px-6 py-6">
          <div className="flex w-full items-center justify-between border-b border-solid border-neutral-border pb-4">
            <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
              <span className="text-heading-2 font-heading-2 text-default-font">
                Voice Message
              </span>
              <span className="text-body font-body text-subtext-color">
                Record a voice message to send
              </span>
            </div>
            <IconButton
              icon="FeatherX"
              onClick={handleCancel}
            />
          </div>

          <div className="flex w-full h-40 items-center justify-center">
            {renderRecordingButton()}
          </div>
        </div>
      </DialogLayout>

      <DialogLayout open={showPreview && !!audioUrl} onOpenChange={() => setShowPreview(false)}>
        <div className="flex h-full w-full flex-col items-start gap-6 bg-default-background px-6 py-6">
          <div className="flex w-full items-center justify-between border-b border-solid border-neutral-border pb-4">
            <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
              <span className="text-heading-2 font-heading-2 text-default-font">
                Voice Message Preview
              </span>
              <span className="text-body font-body text-subtext-color">
                {isUploading ? "Uploading..." : uploadedFile ? "Ready to send" : "Processing..."}
              </span>
            </div>
            <IconButton
              icon="FeatherX"
              onClick={handleCancel}
            />
          </div>

          <div className="relative w-full grow shrink-0 basis-0 flex-col items-center justify-center gap-4 rounded-md border border-solid border-neutral-border bg-neutral-50 px-6 py-6">
            {isUploading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
                  <div className="mt-2 text-neutral-600">Uploading...</div>
                </div>
              </div>
            ) : uploadError ? (
              <div className="text-center text-red-500 p-4">
                {uploadError}
              </div>
            ) : (
              <div className="flex w-full items-center justify-center p-4">
                {audioUrl && (
                  <VoiceMessage 
                    audioUrl={audioUrl} 
                    duration={recordingTime}
                    className="w-full max-w-md"
                    showVolumeControls={true}
                  />
                )}
              </div>
            )}
          </div>

          <div className="flex w-full grow shrink-0 basis-0 flex-col items-center gap-4">
            <div className="flex grow shrink-0 basis-0 items-center rounded-lg bg-brand-600">
              <SubframeCore.Tooltip.Provider>
                <SubframeCore.Tooltip.Root>
                  <SubframeCore.Tooltip.Trigger asChild={true}>
                    <IconButton
                      className="h-auto w-8 flex-none self-stretch"
                      variant="brand-primary"
                      icon="FeatherCalendarClock"
                      onClick={() => setIsDateTimePopupOpen(true)}
                      disabled={!uploadedFile || isUploading}
                    />
                  </SubframeCore.Tooltip.Trigger>
                  <SubframeCore.Tooltip.Portal>
                    <SubframeCore.Tooltip.Content
                      side="bottom"
                      align="center"
                      sideOffset={4}
                      asChild={true}
                    >
                      <Tooltip>Schedule send</Tooltip>
                    </SubframeCore.Tooltip.Content>
                  </SubframeCore.Tooltip.Portal>
                </SubframeCore.Tooltip.Root>
              </SubframeCore.Tooltip.Provider>
              <div className="flex w-px flex-none flex-col items-center gap-2 self-stretch bg-brand-50" />
              <Button
                className="h-auto min-h-[36px] w-auto flex-none self-stretch"
                iconRight="FeatherSendHorizontal"
                onClick={() => handleSend()}
                disabled={isUploading || isMediaUploading || !uploadedFile}
                loading={isMediaUploading}
              >
                Send
              </Button>
            </div>
          </div>
        </div>
      </DialogLayout>

      {/* Date and Time Picker Popup */}
      <SelectDateAndTimePopup
        open={isDateTimePopupOpen}
        setSelectDateAndTimePopupOpen={setIsDateTimePopupOpen}
        onScheduleMessage={handleScheduleMessage}
        messageText="" // No caption for voice messages
      />
    </>
  );
};

export default VoiceRecorder;